import React, { useState } from 'react';
import './SOPModal.css';

interface SOPModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialAccessCode?: string;
}

const SOPModal: React.FC<SOPModalProps> = ({ isOpen, onClose, initialAccessCode }) => {
    const [accessCode, setAccessCode] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sopData, setSopData] = useState<{ title: string; content: string; pdf_path?: string } | null>(null);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Block Ctrl/Cmd + C (Copy), P (Print), S (Save), A (Select All)
            if ((e.ctrlKey || e.metaKey) && ['c', 'p', 's', 'a', 'C', 'P', 'S', 'A'].includes(e.key)) {
                e.preventDefault();
                return false;
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
            if (initialAccessCode) {
                setAccessCode(initialAccessCode);
                verifyCode(initialAccessCode);
            }
        } else {
            // Reset state when closed
            setAccessCode('');
            setIsAuthenticated(false);
            setError('');
            setSopData(null);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, initialAccessCode]);

    if (!isOpen) return null;

    const verifyCode = async (codeToCheck: string) => {
        try {
            const response = await fetch('/api/verify-sop-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: codeToCheck.trim() }),
            });

            const data = await response.json();

            if (data.success) {
                setIsAuthenticated(true);
                setError('');
                fetchSOPContent();
            } else {
                setError(data.message || '코드가 일치하지 않습니다. 영업담당자에게 문의해주세요.');
            }
        } catch (err) {
            console.error('Error verifying code:', err);
            setError('서버 연결 중 오류가 발생했습니다.');
        }
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        verifyCode(accessCode);
    };

    const fetchSOPContent = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/sop/current');
            if (response.ok) {
                const data = await response.json();
                if (data && (data.content || data.pdf_path)) {
                    setSopData(data);
                }
            }
        } catch (err) {
            console.error('Failed to fetch SOP content');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <div className="sop-modal-overlay" onClick={handleClose} onContextMenu={(e) => e.preventDefault()}>
            <div
                className="sop-modal-content"
                onClick={e => e.stopPropagation()}
                style={{ maxWidth: '1000px', width: '90%' }}
                onContextMenu={(e) => e.preventDefault()}
            >
                <button className="sop-modal-close" onClick={handleClose}>&times;</button>

                {!isAuthenticated ? (
                    <div className="sop-auth-container">
                        <h2 className="sop-title">보안 접근 권한이 필요합니다</h2>
                        <p className="sop-notice">
                            납품대행-내국물품운영절차(SOP) 문서는 대외비 정보가 포함되어 있습니다.<br />
                            <span className="highlight">
                                견적요청을 하셔야 (주)천일국제물류 영업담당자에게서<br />
                                상담전화로 접근 코드를 안내받으실 수 있습니다.
                            </span>
                        </p>

                        <form onSubmit={handleLogin} className="sop-auth-form">
                            <input
                                type="password"
                                placeholder="접근 코드 입력"
                                value={accessCode}
                                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                className="sop-input"
                            />
                            <button type="submit" className="btn btn-primary">확인</button>
                        </form>
                        {error && <p className="sop-error">{error}</p>}
                    </div>
                ) : (
                    <div className="sop-document-container" style={{ padding: '0', height: '80vh' }}>
                        {isLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <p>문서를 불러오는 중입니다...</p>
                            </div>
                        ) : sopData ? (
                            sopData.pdf_path ? (
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <iframe
                                        src={(sopData.pdf_path.startsWith('/uploads/') ? sopData.pdf_path.replace('/uploads/', '/api/uploads/') : sopData.pdf_path) + '?t=' + new Date().getTime() + '#toolbar=0&navpanes=0&scrollbar=0'}
                                        style={{ width: '100%', flex: 1, border: 'none' }}
                                        title="SOP Document"
                                    />
                                </div>
                            ) : (
                                <div className="sop-body" dangerouslySetInnerHTML={{ __html: sopData.content }} />
                            )
                        ) : (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                                <p>등록된 운영 절차(SOP) 문서가 없습니다.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SOPModal;
